/*global google */

const fetch = require('node-fetch')
const { Loader } = require('@googlemaps/js-api-loader')
const { formatPostalCode } = require('../../src/helpers/string')

let loaded = false

const initGoogleAPILoader = () => {
  const loader = new Loader({
    apiKey: process.env.VUE_APP_GOOGLE_PUBLIC_API_KEY,
    version: 'weekly',
  })
  loader.load().then(() => {
    loaded = true
  })
}

/**
 * PUBLIC
 */
const getAddressByReverseGeocoding = async ({ coordinates }) => {
  let lng = coordinates[0]
  let lat = coordinates[1]
  let endpoint = 'https://maps.googleapis.com/maps/api/geocode/json'
  let address = await fetch(
    `${endpoint}?latlng=${lat},${lng}&key=${process.env.VUE_APP_GOOGLE_PUBLIC_API_KEY}&language=nl&result_type=street_address|route`,
  )
  address = await address.json()

  // The status is ok if there are results
  if (address.status === 'OK') {

    // See: https://developers.google.com/maps/documentation/geocoding/overview#ReverseGeocoding
    address = address.results[0]

    let components = address.address_components.reduce(componentsToAddressObject, {})

    return enrichAddress({ components, address })
  }

  return false
}

/**
 * May be triggered in realtime based on user interaction
 *  https://developers.google.com/maps/documentation/javascript/geocoding#ReverseGeocoding
 */
const dynamicReverseGeocode = async ({ lat, lng, bilingual = false }) => {
  const latlng = {
    lat: parseFloat(lat),
    lng: parseFloat(lng),
  }

  const validTypes = ['street_address', 'premise', 'subpremise']

  return new Promise((resolve, reject) => {
    if (! loaded) {
      reject('Geocoder is not (yet) available')
    } else {
      const geocoder = new google.maps.Geocoder()

      if (bilingual) {
        const dutchRequest = geocoder.geocode({ location: latlng, language: 'nl' })
        const frenchRequest = geocoder.geocode({ location: latlng, language: 'fr' })

        Promise.all([dutchRequest, frenchRequest]).then(results => {
          const dutchAddress = results[0].results[0]
          const frenchComponents = results[1].results[0].address_components.reduce(componentsToAddressObject, {})
          const dutchComponents = dutchAddress.address_components.reduce(componentsToAddressObject, {})

          dutchComponents.streetFr = frenchComponents?.street
          dutchComponents.cityFr = frenchComponents?.city

          resolve(enrichAddress({ components: dutchComponents, address: dutchAddress }))
        }).catch(reject)
      } else {
        geocoder.geocode({ location: latlng }, (results, status) => {
          if (status === 'OK' && results.length !== 0) {
            results = results.filter(result => validTypes.some(type => result.types.includes(type)))
            if (results.length !== 0) {
              const bestAddress = results[0]
              const components = bestAddress.address_components.reduce(componentsToAddressObject, {})
              resolve(enrichAddress({ components, address: bestAddress }))
            } else {
              reject('not found')
            }
          } else {
            reject(status)
          }
        })
      }
    }
  })
}

/**
 * PRIVATE
 */

const componentsToAddressObject = (result, component) => {

  if (component.types.includes('administrative_area_level_2')) {
    result.municipality = component.long_name
  }
  else if (component.types.includes('administrative_area_level_1')) {
    result.province = component.long_name
  }
  else if (component.types.includes('country')) {
    result.country = component.long_name
    result.country_code = component.short_name
  }
  else if (component.types.includes('postal_code')) {
    result.postalCode = formatPostalCode(component.long_name)
  }
  else if (component.types.includes('route')) {
    result.street = component.long_name
  }
  // This consists of number + suffix...
  else if (component.types.includes('street_number')) {
    result.street_number = component.long_name
  }
  else if (component.types.includes('locality') || component.types.includes('postal_town')) {
    result.city = component.long_name
  }

  return result
}

// ISO country codes where the local authority (commune / Gemeinde) maps to
// Google's `locality` rather than `administrative_area_level_2`, so we prefer
// the city over the level_2 value for these:
// - BE: Google returns no administrative_area_level_2 for communes.
// - DE: administrative_area_level_2 is the Landkreis (district), not the
//       Gemeinde; for kreisfreie Städte (e.g. Frankfurt (Oder)) it is absent
//       entirely, which previously left municipality null.
const MUNICIPALITY_FROM_LOCALITY_COUNTRY_CODES = new Set([
  'BE',
  'DE',
])

const resolveMunicipality = (components) => {
  if (MUNICIPALITY_FROM_LOCALITY_COUNTRY_CODES.has(components.country_code)) {
    return components.city ?? components.municipality ?? null
  }
  return components.municipality ?? null
}

const enrichAddress = ({ components, address }) => ({
  source: 'Google',
  runCount: 1, // This is the geocoder version.

  postalcode: components.postalCode ?? null,
  street: components.street ?? null,
  number: components.street_number ?? null,
  municipality: resolveMunicipality(components),
  city: components.city ?? null,
  country: components.country ?? null,
  province: components.province ?? null,

  ...(components.streetFr !== undefined ? { streetFr: components.streetFr } : {}),
  ...(components.cityFr !== undefined ? { cityFr: components.cityFr } : {}),

  simple_address: `${components.street ?? ''} ${components.street_number ?? ''}`.trim(), // e.g. 'Helmkruid 33'
  formatted_address: address.formatted_address, // e.g. 'Helmkruid 33, 5684 HP Best, Nederland'
  coordinates: address.geometry.location, // e.g. {"lat": 51.4957034,"lng": 5.3889716} // the geolocation lat lng
})

module.exports = {
  initGoogleAPILoader,
  getAddressByReverseGeocoding,
  dynamicReverseGeocode,
}
